import { Map } from 'immutable';

import { shapeFileForApp } from '../utils/studio/shapeForApp';
import { increase } from '../utils/math';
import { getSize } from '../utils/iterable/getSize';
import api from '../utils/api';

import { ETSY, SHOPIFY } from '../constants/channels';
import { MEDIA_TYPE, ORDER_BY } from '../constants/studio';
import { FEATURE } from '../constants/features';
import { EMPTY } from '../constants';

import studioPhotos from './studio.photos';
import studioVideos from './studio.videos';
import actions from '../actions';

const STUDIO_STORE_KEY = {
  [MEDIA_TYPE.PHOTOS]: 'photos',
  [MEDIA_TYPE.VIDEOS]: 'videos',
};

function* uploadFiles(state, payload) {
  const { channel, files, folder, shopId, type, uploaded = [], ...rest } = payload;
  let uploading = state.getIn(['studio', 'uploading']);

  if (getSize(files) === 0) {
    if (
      getSize(uploading.getIn([shopId, type, folder || null, 'byId'])) === 0 &&
      getSize(uploading.getIn([shopId, type, folder || null, 'ids'])) === 0
    ) {
      uploading = uploading.deleteIn([shopId, type, folder || null]);
    }

    if (getSize(uploading.getIn([shopId, type])) === 0) {
      uploading = uploading.deleteIn([shopId, type]);
    }

    if (getSize(uploading.get(shopId)) === 0) {
      uploading = uploading.delete(shopId);
    }

    yield function effect(dispatch) {
      const { action, error, total } = rest;

      if (getSize(uploaded) > 0) {
        dispatch(action.fulfilled({ error, folder, shopId, total, uploaded }));
      } else {
        dispatch(action.rejected({ error, folder, shopId, total, uploaded }));
      }
    };

    return state.setIn(['studio', 'uploading'], uploading);
  }

  yield function effect(dispatch) {
    function onError(newError) {
      dispatch(
        actions.studio.uploadFiles({
          ...rest,
          channel,
          error: newError || rest.error,
          files: files.slice(1),
          folder,
          shopId,
          type,
          uploaded,
        })
      );
    }

    function onSuccess(file) {
      dispatch(
        actions.studio.uploadFiles.fulfilled({
          ...rest,
          channel,
          file,
          files: files.slice(1),
          folder,
          shopId,
          type,
          uploaded,
        })
      );
    }

    shapeFileForApp({ channel, file: files.first(), type })
      .then(onSuccess)
      .catch(onError);
  };

  return state;
}

function* uploadFilesFulfilled(state, payload) {
  const { channel, db, file, files, folder, shopId, type, uploaded = [], ...rest } = payload;

  function updateById(byId = EMPTY.MAP) {
    return byId.set(file.uuid, Map(file));
  }

  function updateIds(ids = EMPTY.LIST) {
    function filterIds(id) {
      return id !== file.updloadId;
    }

    return file.updloadId
      ? ids.filter(filterIds)
      : ids.push(file.uuid);
  }

  if (file.updloadId) {
    yield function effect(dispatch) {
      dispatch(
        actions.studio.uploadFiles({
          ...rest,
          channel,
          db,
          files,
          folder,
          shopId,
          type,
          uploaded,
        })
      );
    };

    let studio = state.get('studio');
    const key = STUDIO_STORE_KEY[type];

    if (shopId === studio.getIn([key, 'shopId'])) {
      if (folder) {
        studio = studio
          .updateIn([key, 'folders', 'byId', null, 'count'], increase)
          .updateIn([key, 'folders', 'byId', folder, 'count'], increase);
      } else {
        studio = studio.updateIn([key, 'folders', 'byId', null, 'count'], increase);
      }

      if (
        !studio.getIn([key, 'request', 'processing']) &&
        folder === studio.getIn([key, 'request', 'folder', 'uuid'])
      ) {
        studio = studio
          .setIn([key, 'items', 'byId', file.uuid], Map(file))
          .updateIn([key, 'items', 'total'], increase)
          .updateIn([key, 'items', 'ids'], function updateStudioIds(ids) {
            return (
              studio.getIn([key, 'order', 'by']) === ORDER_BY.CREATED_AT &&
              !studio.getIn([key, 'order', 'type'])
            )
              ? ids.unshift(file.uuid)
              : ids.push(file.uuid);
          });
      }
    }

    studio = studio
      .deleteIn(['uploading', shopId, type, folder || null, 'byId', file.updloadId])
      .updateIn(['uploading', shopId, type, folder || null, 'ids'], updateIds);

    return state.set('studio', studio);
  }

  yield function effect(dispatch) {
    const { uuid: updloadId } = file;

    function onFail(error) {
      dispatch(
        actions.studio.uploadFiles.rejected({
          ...rest,
          channel,
          db,
          error,
          files,
          folder,
          id: updloadId,
          shopId,
          type,
          uploaded,
        })
      );
    }

    function onUploaded(uploadedFile) {
      function saveToStudio(props = {}) {
        function onSaved(saved) {
          const updated = {
            ...file,
            ...props,
            ...saved,
            ...uploadedFile,
            updloadId,
          };

          dispatch(
            actions.studio.uploadFiles.fulfilled({
              ...rest,
              channel,
              db,
              file: updated,
              files,
              folder,
              shopId,
              type,
              uploaded: uploaded.concat(updated),
            })
          );
        }

        api.studio.saveFile({ db, folder, file: { ...file, ...props, ...uploadedFile }, shopId, type })
          .then(onSaved)
          .catch(onFail);
      }

      function onAltTextGenerated({ altText }) {
        saveToStudio({ altText: altText.trim().replace(/\s+/g, ' ') });
      }

      switch (type) {
        case MEDIA_TYPE.PHOTOS: {
          switch (channel) {
            case ETSY:
            case SHOPIFY: {
              const v2 = state.getIn(['user', 'features', FEATURE.AI_V2]);

              api.ai.generateAltText({ image: uploadedFile.url, v2, channel, db, shopId })
                .then(onAltTextGenerated)
                .catch(onFail);

              break;
            }

            default: {
              saveToStudio();
              break;
            }
          }

          break;
        }

        case MEDIA_TYPE.VIDEOS: {
          saveToStudio();
          break;
        }

        default: {
          break;
        }
      }
    }

    api.studio.uploadFile({ db, file, shopId, type })
      .then(onUploaded)
      .catch(onFail);
  };

  return state
    .updateIn(['studio', 'uploading', shopId, type, folder || null, 'byId'], updateById)
    .updateIn(['studio', 'uploading', shopId, type, folder || null, 'ids'], updateIds);
}

function* uploadFilesRejected(state, payload) {
  const { error, folder, id, shopId, type, ...rest } = payload;

  yield function effect(dispatch) {
    console.error(error);
    dispatch(actions.studio.uploadFiles({ ...rest, error, folder, shopId, type }));
  };

  function updateIds(ids = EMPTY.LIST) {
    return ids.filter(function filterIds(item) {
      return item !== id;
    });
  }

  if (id) {
    return state
      .deleteIn(['studio', 'uploading', shopId, type, folder || null, 'byId', id])
      .updateIn(['studio', 'uploading', shopId, type, folder || null, 'ids'], updateIds);
  }

  return state;
}

const studio = {
  [actions.studio.uploadFiles]: uploadFiles,
  [actions.studio.uploadFiles.fulfilled]: uploadFilesFulfilled,
  [actions.studio.uploadFiles.rejected]: uploadFilesRejected,
  ...studioPhotos,
  ...studioVideos,
};

export default studio;
